import {
  DashboardApprovalDocumentItem,
  DocumentVersionApproverStatus,
} from "@models";
import { Box, Link, Skeleton, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { userStatusDictionary } from "src/components/DocViewer/components/docInfoDrawer/utils";
import { DocumentStatusIcon } from "src/components/DocumentStatusIcon";
import { UserAvatar } from "src/components/UserAvatar";
import { buildDocumentRoute } from "src/utils";
import { docStatusDictionary } from "src/utils/documents";
import { DashboardEmptyStateText } from "./DashboardEmptyStateText";
import { DASHBOARD_ITEM_PADDING_CLASS_NAME } from "./dashboardItemPadding";

type DashboardApprovalDocumentsListProps = {
  documents: DashboardApprovalDocumentItem[];
  isLoading: boolean;
  error?: unknown;
  orgId: string;
  selectedDeviceId?: string;
  fallbackDeviceId?: string;
  emptyText: string;
  loadingTestId: string;
  statusIconTestIdPrefix: string;
};

type DashboardWaitingApprovalDocumentsListProps = Omit<
  DashboardApprovalDocumentsListProps,
  "emptyText" | "loadingTestId" | "statusIconTestIdPrefix"
>;

const skeletonRowIds = [
  "dashboard-waiting-approval-skeleton-1",
  "dashboard-waiting-approval-skeleton-2",
  "dashboard-waiting-approval-skeleton-3",
];

const statusAvatarColors = {
  success: {
    backgroundColor: "#22c55e",
    textColor: "#ffffff",
  },
  warning: {
    backgroundColor: "#ed6c02",
    textColor: "#ffffff",
  },
  default: {
    backgroundColor: "rgba(0, 0, 0, 0.54)",
    textColor: "#ffffff",
  },
};

const getDocumentDeviceId = (
  document: DashboardApprovalDocumentItem,
  selectedDeviceId?: string,
  fallbackDeviceId?: string
) => document.deviceId ?? selectedDeviceId ?? fallbackDeviceId;

const getUserName = (user: {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email;
};

const getApproverAvatarColors = (approvalStatus: string | null) => {
  const statusColor = approvalStatus
    ? userStatusDictionary[approvalStatus]?.variant
    : undefined;

  if (statusColor === "success") {
    return statusAvatarColors.success;
  }

  if (statusColor === "warning") {
    return statusAvatarColors.warning;
  }

  return statusAvatarColors.default;
};

const getDashboardApproverStatusText = (
  approver: DashboardApprovalDocumentItem["approvers"][number]
) => {
  if (approver.approvalStatus === DocumentVersionApproverStatus.APPROVED) {
    const dateTime = new Date(approver.updatedAt).toISOString().slice(0, 16);
    return `${userStatusDictionary[approver.approvalStatus].text} ${dateTime}(UTC)`;
  }

  return approver.approvalStatus
    ? (userStatusDictionary[approver.approvalStatus]?.text ??
        "Pending approval")
    : "Pending approval";
};

export const DashboardApprovalDocumentsList = ({
  documents,
  isLoading,
  error,
  orgId,
  selectedDeviceId,
  fallbackDeviceId,
  emptyText,
  loadingTestId,
  statusIconTestIdPrefix,
}: DashboardApprovalDocumentsListProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid={loadingTestId}>
        {skeletonRowIds.map((rowId) => (
          <Skeleton key={rowId} variant="rectangular" height={38} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Failed to load documents. Please try again.
      </Typography>
    );
  }

  if (documents.length === 0) {
    return <DashboardEmptyStateText>{emptyText}</DashboardEmptyStateText>;
  }

  return (
    <Box className="flex flex-col gap-1">
      {documents.map((document) => {
        const status = document.status;
        const statusTooltipTitle = docStatusDictionary[status].label;
        const approvers = document.approvers;
        const title = document.title;
        const deviceId = getDocumentDeviceId(
          document,
          selectedDeviceId,
          fallbackDeviceId
        );
        const documentRoute = deviceId
          ? buildDocumentRoute({
              orgId,
              deviceId,
              documentId: document.documentId,
              templateKey: document.templateKey,
            })
          : undefined;
        const rowContent = (
          <>
            <Box className="flex min-w-0 flex-1 items-center gap-2">
              <Tooltip describeChild placement="top" title={statusTooltipTitle}>
                <span className="inline-flex">
                  <DocumentStatusIcon
                    reserveSpace
                    size={8}
                    status={status}
                    testId={`${statusIconTestIdPrefix}-${document.documentId}`}
                  />
                </span>
              </Tooltip>
              <Typography
                variant="body2"
                className="min-w-0 truncate"
                sx={{ lineHeight: "20px" }}
              >
                <Box component="span">{title}</Box>
              </Typography>
            </Box>
            <Box className="ml-auto flex shrink-0 items-center">
              {approvers.map((approver, index) => {
                const statusText = getDashboardApproverStatusText(approver);
                const approverName = getUserName(approver.user);
                const avatarColors = getApproverAvatarColors(
                  approver.approvalStatus
                );

                return (
                  <Tooltip
                    describeChild
                    key={approver.id}
                    placement="top"
                    title={
                      <Box>
                        <Typography variant="caption" component="div">
                          {approverName}
                        </Typography>
                        <Typography variant="caption" component="div">
                          {statusText}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box
                      component="span"
                      data-testid={`dashboard-approver-avatar-${approver.id}`}
                      className={[
                        "relative inline-flex rounded-full bg-white p-0.5",
                        index > 0 ? "-ml-1.5" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      sx={{ zIndex: approvers.length - index }}
                    >
                      <UserAvatar
                        userId={approver.user.id}
                        name={approverName}
                        size={24}
                        fontSize={10}
                        backgroundColor={avatarColors.backgroundColor}
                        textColor={avatarColors.textColor}
                        ariaLabel={`${approverName}: ${statusText}`}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </>
        );

        if (!documentRoute) {
          return (
            <Box
              key={document.documentId}
              className={[
                "flex min-w-0 items-center gap-2 rounded",
                DASHBOARD_ITEM_PADDING_CLASS_NAME,
              ].join(" ")}
            >
              {rowContent}
            </Box>
          );
        }

        return (
          <Link
            key={document.documentId}
            component={RouterLink}
            to={documentRoute}
            underline="none"
            color="text.primary"
            className={[
              "flex min-w-0 items-center gap-2 rounded hover:bg-gray-50",
              DASHBOARD_ITEM_PADDING_CLASS_NAME,
            ].join(" ")}
          >
            {rowContent}
          </Link>
        );
      })}
    </Box>
  );
};

export const DashboardWaitingApprovalDocumentsList = (
  props: DashboardWaitingApprovalDocumentsListProps
) => (
  <DashboardApprovalDocumentsList
    {...props}
    emptyText="No documents waiting for approval."
    loadingTestId="dashboard-waiting-approval-documents-loading"
    statusIconTestIdPrefix="dashboard-waiting-approval-document-status-icon"
  />
);
